// THE DISCIPLINE PICKER'S OPTIONS: each discipline, and each contractor
// under it.
//
// Owner, 2026-10-15: "add the tags in set fields for bulk issues, and the
// dicipline, should list each contractor by discipline and fields
// dicipline/contractor and tag should be visible inside each issue".
//
// One group per discipline, headed by its name. Inside it, first the
// discipline on its own, then every partner company whose trades on this
// project include it - read from the same roster every people picker reads
// (GET /projects/:id/participants/roster, back/utils/pickerRoster), so the
// two can never list different partners. A partner with three trades is
// listed under all three.
//
// WHAT A PICK MEANS:
//   the discipline alone   sets the issue's discipline, leaves its
//                          contractor as it is
//   a contractor under it  sets the discipline AND the contractor
//                          (issue.responsibleCompanyId) in one go
//
// Values are strings so antd can hold them: "d|<key>" and
// "c|<key>|<companyId>". parsePick() turns one back into the fields it
// sets; pickValue() goes the other way for an issue that already has them.
//
// NOBODY DISAPPEARS. A discipline already on an issue but on neither list is
// added under "Not on the project's lists" rather than shown blank, and a
// partner with no trades on the project is counted in `untraded` so the
// dropdown can say so instead of quietly listing fewer contractors.
// Pure, so the bulk modal, Add multiple, the issue page and the tests share
// exactly one answer.

const s = (v) => String(v === null || v === undefined ? "" : v).trim();

export const NONE_VALUE = "none";

export function disciplineValue(key) { return `d|${s(key)}`; }
export function contractorValue(key, companyId) { return `c|${s(key)}|${s(companyId)}`; }

/** "c|E|abc" -> { discipline: "E", responsibleCompanyId: "abc" }; "d|E" -> { discipline: "E" }. */
export function parsePick(value) {
  if (value === null || value === undefined) return null;
  const v = String(value);
  if (v === NONE_VALUE) return { discipline: "" };
  if (v.startsWith("c|")) {
    const rest = v.slice(2);
    const at = rest.lastIndexOf("|");
    return { discipline: rest.slice(0, at), responsibleCompanyId: rest.slice(at + 1) };
  }
  if (v.startsWith("d|")) return { discipline: v.slice(2) };
  return { discipline: v };
}

/**
 * disciplineContractorOptions({ disciplines, roster, current })
 *   disciplines  the owning company's list [{ key, value }] (models/discipline)
 *   roster       the picker roster groups
 *   current      { discipline } already on the issue, kept visible if unknown
 * -> { groups, untraded, contractorCount }
 */
export function disciplineContractorOptions({ disciplines = [], roster = [], current = null } = {}) {
  const order = [];
  const labelOf = new Map();          // lower-case key -> { key, label }
  const add = (key, label) => {
    const k = s(key);
    if (!k) return;
    const low = k.toLowerCase();
    if (labelOf.has(low)) return;
    labelOf.set(low, { key: k, label: s(label) || k });
    order.push(low);
  };
  // The company's own list first: it is the canonical spelling.
  for (const d of disciplines || []) {
    if (!d || d.deleted) continue;
    add(d.key || d.value, d.value || d.key);
  }
  const partners = (roster || []).filter((g) => g && g.role !== "owner" && s(g.companyId));
  const byKey = new Map();            // lower-case key -> [group]
  let untraded = 0;
  for (const g of partners) {
    const trades = (g.trades || []).map(s).filter(Boolean);
    if (!trades.length) { untraded += 1; continue; }
    trades.forEach((t, i) => {
      add(t, (g.tradeLabels || [])[i] || t);
      const low = t.toLowerCase();
      if (!byKey.has(low)) byKey.set(low, []);
      if (!byKey.get(low).some((x) => s(x.companyId) === s(g.companyId))) byKey.get(low).push(g);
    });
  }
  let contractorCount = 0;
  const groups = order.map((low) => {
    const { key, label } = labelOf.get(low);
    const name = label !== key ? `${label} (${key})` : label;
    const companies = (byKey.get(low) || []).slice().sort((a, b) => s(a.companyName).localeCompare(s(b.companyName)));
    contractorCount += companies.length;
    return {
      label: name,
      title: name,
      options: [
        // `partners` / `trades` (owner, 2026-10-16): the discipline with the
        // partners who do it beneath, each partner with ALL its trades
        // beneath - drawn by the picker's optionRender, searched here.
        {
          value: disciplineValue(key), label: `${name} - discipline only`, display: name,
          partners: companies.map((g) => s(g.companyName) || "Partner"),
          search: `${name} ${key} ${companies.map((g) => s(g.companyName)).join(" ")}`.toLowerCase(),
        },
        ...companies.map((g) => {
          const trades = ((g.tradeLabels && g.tradeLabels.length ? g.tradeLabels : g.trades) || []).map(s).filter(Boolean);
          return {
            value: contractorValue(key, g.companyId),
            label: s(g.companyName) || "Partner",
            display: `${name} · ${s(g.companyName) || "Partner"}`,
            trades,
            search: `${name} ${key} ${s(g.companyName)} ${trades.join(" ")}`.toLowerCase(),
          };
        }),
      ],
    };
  });
  const cur = s(current && current.discipline);
  if (cur && !labelOf.has(cur.toLowerCase())) {
    groups.push({
      label: "Not on the project's lists",
      options: [{ value: disciplineValue(cur), label: `${cur} - typed`, display: cur, search: cur.toLowerCase() }],
    });
  }
  return { groups, untraded, contractorCount };
}

/** The option value that shows what an issue already has. */
export function pickValue(groups, { discipline, responsibleCompanyId } = {}) {
  const d = s(discipline);
  if (!d) return undefined;
  const all = (groups || []).flatMap((g) => g.options || []);
  if (s(responsibleCompanyId)) {
    const c = all.find((o) => o.value.toLowerCase() === contractorValue(d, responsibleCompanyId).toLowerCase());
    if (c) return c.value;
  }
  const hit = all.find((o) => o.value.toLowerCase() === disciplineValue(d).toLowerCase());
  return hit ? hit.value : disciplineValue(d);
}

/** Search on discipline AND company: typing "Electrical" finds its contractors too. */
export function filterPick(input, option) {
  const q = s(input).toLowerCase();
  if (!q) return true;
  return String((option && (option.search || option.label)) || "").toLowerCase().includes(q);
}
